import { observer } from 'mobx-react-lite'
import { TouchableOpacity } from 'react-native'
import { debounce, get } from 'lodash'
import { DS } from '../../../design-system'
import { SettingsSVG } from './assets/SettingsSVG'
import { CloseSVG } from './assets/CloseSVG'
import { GiveChipsSVG } from './assets/GiveChipsSVG'
import { ShowCardsSVG } from './assets/ShowCardsSVG'
import { AddBotsSVG } from './assets/AddBotsSVG'
import { EndGameSVG } from './assets/EndGameSVG'
import { PauseSVG } from './assets/PauseSVG'
import { BoardStates, canReceiveChips, Palette, Player } from '@ppwr/poker'
import { create } from 'zustand'
import { StyleSheet, Platform } from 'react-native'
import Modal from 'react-native-modal'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { memo, ReactNode, useCallback, useRef } from 'react'
import { useStore } from '../../../models'
import { ResumeSVG } from './assets/ResumeSVG'
import { useRemoteConfig } from '../../../../module/remote-config/useRemoteConfig'
import { useThrottledCallback } from 'use-debounce'

enum CurrentTeacherOptions {
  showCards = 'showCards',
  endGame = 'endGame',
  giveChips = 'giveChips',
  addBots = 'addBots',
  addBotsFailed = 'addBotsFailed'
}

interface TeacherControlStore {
  isTeacherControlOpen: boolean
  toggleIsTeacherControl: () => void
  autoRestart: boolean
  toggleAutoRestart: () => void
  currentTeacherOption?: CurrentTeacherOptions
  setCurrentTeacherOption: (option: CurrentTeacherOptions) => void
  removeCurrentTeacherOption: () => void
  botsCount: number
  setBotsCount: (count: number) => void
}

export const useTeacherControl = create<TeacherControlStore>((set) => ({
  isTeacherControlOpen: false,
  toggleIsTeacherControl: () => set((state) => ({ isTeacherControlOpen: !state.isTeacherControlOpen })),
  autoRestart: false,
  toggleAutoRestart: () => set((state) => ({ autoRestart: !state.autoRestart })),
  setCurrentTeacherOption: (option) =>
    set(() => ({
      currentTeacherOption: option
    })),
  removeCurrentTeacherOption: () =>
    set(() => ({
      currentTeacherOption: undefined
    })),
  showCardsPrivately: false,
  botsCount: 0,
  setBotsCount: (count) => set(() => ({ botsCount: count }))
}))

interface PanelButtonProps {
  icon: ReactNode
  label: string
  onPress: () => void
  bgColor?: string
  disabledBgColor?: string
}

const PanelButton = memo(
  ({
    icon,
    label,
    onPress,
    bgColor,
    disabledBgColor = Palette['Brand/Light Grey'],
    disabled,
    ...rest
  }: React.ComponentProps<typeof TouchableOpacity> & PanelButtonProps) => {
    return (
      <TouchableOpacity
        style={[styles.panelButton, { backgroundColor: disabled ? disabledBgColor : bgColor }]}
        onPress={onPress}
        disabled={disabled}
        {...rest}
      >
        {icon}
        <DS.Text variant="Mini/Medium" style={styles.panelButtonLabel}>
          {label}
        </DS.Text>
      </TouchableOpacity>
    )
  }
)

const ConfirmPanel = ({
  onPressBack,
  label,
  children
}: { onPressBack: () => void; label: string; children: ReactNode }) => {
  return (
    <DS.Frame style={styles.panelPlayerContentRow}>
      <DS.Frame style={styles.panelPlayerConfirmLabelContainer}>
        <TouchableOpacity onPress={onPressBack}>
          <DS.Icon name="Arrow---left" width={20} height={20} color="Global/Black" />
        </TouchableOpacity>
        <DS.Text variant="Body Medium/Medium" numberOfLines={2} ellipsizeMode="tail">
          {label}
        </DS.Text>
      </DS.Frame>
      <DS.Frame style={styles.panelPlayerConfirmButtonContainer}>{children}</DS.Frame>
    </DS.Frame>
  )
}

const ShowCards = memo(
  ({
    onPressBack,
    onPressPublic,
    onPressPrivate
  }: { onPressBack: () => void; onPressPublic: () => void; onPressPrivate: () => void }) => {
    return (
      <ConfirmPanel onPressBack={onPressBack} label="Show Cards">
        <DS.Button
          style={{ width: 90, height: 38 }}
          textColor="Global/Black"
          variant="Medium/Secondary"
          type="On Light"
          onPress={onPressPublic}
        >
          Public
        </DS.Button>
        <DS.Button style={{ width: 93, height: 38 }} variant="Medium/Primary" type="On Light" onPress={onPressPrivate}>
          Private
        </DS.Button>
      </ConfirmPanel>
    )
  }
)

const GiveChips = memo(
  ({
    onPressBack,
    onPressGiveChip,
    amounts
  }: {
    onPressBack: () => void
    onPressGiveChip: (amount: number) => void
    amounts: number[]
  }) => {
    return (
      <ConfirmPanel onPressBack={onPressBack} label="Give Chips">
        {amounts.map((amount) => (
          <DS.Button
            key={amount}
            style={{ paddingHorizontal: DS.spacing[24], height: 38 }}
            centerContent
            textColor="Global/Black"
            variant="Medium/Secondary"
            type="On Light"
            onPress={() => onPressGiveChip(amount * 1000)}
          >
            {amount}k
          </DS.Button>
        ))}
      </ConfirmPanel>
    )
  }
)

const TeacherControlPanel = observer(() => {
  const insets = useSafeAreaInsets()
  const {
    isTeacherControlOpen,
    toggleIsTeacherControl,
    autoRestart,
    toggleAutoRestart,
    setCurrentTeacherOption,
    removeCurrentTeacherOption,
    currentTeacherOption,
    botsCount,
    setBotsCount
  } = useTeacherControl()
  const {
    game: {
      isCardsVisibleByEveryone,
      isCardsVisibleByTeacher,
      hideCards,
      showCardsForTeacher,
      showCardsForEveryone,
      gameStartTime,
      boardState,
      gameId,
      doBuyForAllPlayers,
      addBots,
      defaultTable,
      seatedPlayers,
      isGameOver,
      isPreGame,
      isRegistrationOver,
      isTeacherPlayingInPrivateGame,
      isTeacherInPrivateGame
    },
    communication: { terminateGame },
    session: { user }
  } = useStore()

  // const AutoRestartIcon = ({ label }: { label: string }) => {
  //   return (
  //     <DS.Frame
  //       style={{
  //         backgroundColor: DS.palette['Brand/Light Grey'],
  //         paddingHorizontal: DS.spacing[12],
  //         paddingVertical: DS.spacing[4],
  //         borderRadius: DS.spacing[8]
  //       }}
  //     >
  //       <DS.Text variant="Mini/Bold">{label}</DS.Text>
  //     </DS.Frame>
  //   )
  // }

  // const onPressAutoRestart = () => {
  //   toggleAutoRestart()
  //   setTimeout(() => {
  //     toggleIsTeacherControl()
  //   }, 1000)
  // }

  if (!isTeacherInPrivateGame) return null

  const isShowCardsDisabled =
    boardState === BoardStates.Showdown.tag || !!isTeacherPlayingInPrivateGame || isGameOver || isPreGame
  const isCardsShowing = isCardsVisibleByTeacher || isCardsVisibleByEveryone

  const showCardsProps = {
    disabled: isShowCardsDisabled,
    icon: <ShowCardsSVG />,
    label: isCardsShowing ? 'HIDE CARDS' : 'SHOW CARDS',
    onPress: () => (isCardsShowing ? onHideCards() : setCurrentTeacherOption(CurrentTeacherOptions.showCards))
  }

  const endGameProps = {
    disabled: isGameOver,
    bgColor: isGameOver ? Palette['Brand/Light Grey'] : 'transparent',
    icon: <EndGameSVG />,
    label: 'END GAME',
    onPress: isGameOver ? () => null : () => setCurrentTeacherOption(CurrentTeacherOptions.endGame)
  }

  const isTableFull = defaultTable && seatedPlayers.length >= defaultTable.maxPlayers
  const isAddBotsDisabled =
    isRegistrationOver || isTableFull || isGameOver || !seatedPlayers.length || isTeacherPlayingInPrivateGame

  const addBotsProps = {
    disabled: isAddBotsDisabled,
    bgColor: isAddBotsDisabled ? Palette['Brand/Light Grey'] : 'transparent',
    icon: <AddBotsSVG />,
    label: 'ADD BOTS',
    onPress: isAddBotsDisabled
      ? () => null
      : () => {
          setBotsCount(1)
          setCurrentTeacherOption(CurrentTeacherOptions.addBots)
        }
  }

  const isGiveChipsDisabled = !!isTeacherPlayingInPrivateGame || isGameOver || isPreGame
  const giveChipsProps = {
    disabled: isGiveChipsDisabled,
    icon: <GiveChipsSVG />,
    label: 'GIVE CHIPS',
    onPress: isGiveChipsDisabled ? () => null : () => setCurrentTeacherOption(CurrentTeacherOptions.giveChips)
  }

  const onShowCardsPublicly = () => {
    if (gameStartTime > 0) {
      showCardsForEveryone()
    }

    removeCurrentTeacherOption()
    toggleIsTeacherControl()
  }

  const onShowCardsPrivately = () => {
    if (gameStartTime > 0) {
      showCardsForTeacher()
    }
    removeCurrentTeacherOption()
    toggleIsTeacherControl()
  }

  const onHideCards = () => {
    hideCards()
    toggleIsTeacherControl()
  }

  const onPressEndGame = async () => {
    const userId = user?._id
    if (gameId && userId) {
      await terminateGame(gameId, userId)
    }
    toggleIsTeacherControl()
    removeCurrentTeacherOption()
  }

  const onPressAddBots = debounce(async () => {
    let shouldRunCleanup = true
    try {
      await addBots(botsCount)
    } catch (error) {
      if (get(error, 'response.errors[0].message') === 'Invalid bot count.') {
        setCurrentTeacherOption(CurrentTeacherOptions.addBotsFailed)
        shouldRunCleanup = false
      }
    } finally {
      if (shouldRunCleanup) {
        toggleIsTeacherControl()
        removeCurrentTeacherOption()
      }
    }
  }, 250)

  const onPressAddBotsFailed = async (count: number) => {
    try {
      await addBots(count)
      toggleIsTeacherControl()
      removeCurrentTeacherOption()
    } catch (error) {
      setCurrentTeacherOption(CurrentTeacherOptions.addBotsFailed)
    }
  }

  const EndGame = () => (
    <ConfirmPanel onPressBack={removeCurrentTeacherOption} label="End game for all?">
      <DS.Button
        style={{ width: 68, height: 38 }}
        textColor="Global/Black"
        variant="Medium/Secondary"
        type="On Light"
        onPress={removeCurrentTeacherOption}
      >
        No
      </DS.Button>
      <DS.Button style={{ width: 71, height: 38 }} variant="Medium/Primary" type="On Light" onPress={onPressEndGame}>
        Yes
      </DS.Button>
    </ConfirmPanel>
  )

  const onPressGiveChipsToTable = debounce((amount: number) => {
    doBuyForAllPlayers(amount)
    toggleIsTeacherControl()
    removeCurrentTeacherOption()
  })

  const AddBots = () => (
    <DS.Frame style={styles.panelPlayerContentRow}>
      <DS.Frame style={styles.panelPlayerConfirmLabelContainer}>
        <DS.Frame style={styles.panelStepperContainer}>
          <TouchableOpacity
            style={styles.panelStepperButton}
            onPress={() => botsCount > 1 && setBotsCount(botsCount - 1)}
          >
            <DS.Icon name="Minus--small" width={24} height={24} color="Global/Black" />
          </TouchableOpacity>
          <DS.Frame style={{ width: 1, height: 24, backgroundColor: DS.palette['Brand/Cream'] }} />
          <TouchableOpacity
            style={styles.panelStepperButton}
            onPress={() => botsCount + seatedPlayers.length < defaultTable.maxPlayers && setBotsCount(botsCount + 1)}
          >
            <DS.Icon name="Plus--small" width={24} height={24} color="Global/Black" />
          </TouchableOpacity>
        </DS.Frame>

        <DS.Text variant="Body Medium/Medium" numberOfLines={2} ellipsizeMode="tail">
          {`Add ${botsCount} bot${botsCount > 1 ? 's' : ''}`}
        </DS.Text>
      </DS.Frame>
      <DS.Frame style={styles.panelPlayerConfirmButtonContainer}>
        <DS.Button
          style={{ width: 71, height: 38 }}
          textColor="Global/Black"
          variant="Medium/Secondary"
          type="On Light"
          onPress={removeCurrentTeacherOption}
        >
          No
        </DS.Button>
        <DS.Button style={{ width: 71, height: 38 }} variant="Medium/Primary" type="On Light" onPress={onPressAddBots}>
          Yes
        </DS.Button>
      </DS.Frame>
    </DS.Frame>
  )

  const AddBotsFailed = () => (
    <ConfirmPanel
      onPressBack={() => {
        setBotsCount(defaultTable.maxPlayers - seatedPlayers.length)
        setCurrentTeacherOption(CurrentTeacherOptions.addBots)
      }}
      label={`Not enough seats for ${botsCount} bot${botsCount > 1 ? 's' : ''}`}
    >
      <DS.Button
        style={{ width: 88, height: 38 }}
        textColor="Global/Black"
        variant="Medium/Secondary"
        type="On Light"
        onPress={() => {
          onPressAddBotsFailed(defaultTable.maxPlayers - seatedPlayers.length)
        }}
      >
        Add {defaultTable.maxPlayers - seatedPlayers.length}
      </DS.Button>
    </ConfirmPanel>
  )

  const ConfirmOptions = {
    [CurrentTeacherOptions.showCards]: (
      <ShowCards
        onPressBack={removeCurrentTeacherOption}
        onPressPrivate={onShowCardsPrivately}
        onPressPublic={onShowCardsPublicly}
      />
    ),
    [CurrentTeacherOptions.endGame]: <EndGame />,
    [CurrentTeacherOptions.giveChips]: (
      <GiveChips
        onPressBack={removeCurrentTeacherOption}
        onPressGiveChip={onPressGiveChipsToTable}
        amounts={[2, 5, 10]}
      />
    ),
    [CurrentTeacherOptions.addBots]: <AddBots />,
    [CurrentTeacherOptions.addBotsFailed]: <AddBotsFailed />
  }

  const TeacherOptions = () => {
    return (
      <>
        <DS.Frame style={styles.panelContentRow}>
          <PanelButton
            icon={giveChipsProps.icon}
            label={giveChipsProps.label}
            onPress={giveChipsProps.onPress}
            disabled={giveChipsProps.disabled}
          />
          <PanelButton
            icon={showCardsProps.icon}
            label={showCardsProps.label}
            onPress={showCardsProps.onPress}
            disabled={showCardsProps.disabled}
          />
          <PanelButton
            icon={addBotsProps.icon}
            label={addBotsProps.label}
            onPress={addBotsProps.onPress}
            disabled={addBotsProps.disabled}
          />
        </DS.Frame>
        <DS.Frame style={styles.panelContentRow}>
          {/* <PanelButton
            bgColor={buttonBgColor}
            icon={<AutoRestartIcon label={autoRestart ? 'ON' : 'OFF'} />}
            label="AUTO RESTART GAME"
            onPress={onPressAutoRestart}
            disabled={isButtonDisabled}
          />
          <PanelButton
            icon={<RestartGameSVG />}
            label="RESTART GAME"
            onPress={toggleIsTeacherControl}
            disabled={isButtonDisabled}
          /> */}
          <PanelButton
            icon={endGameProps.icon}
            label={endGameProps.label}
            onPress={endGameProps.onPress}
            disabled={endGameProps.disabled}
          />
        </DS.Frame>
      </>
    )
  }

  return (
    <Modal
      onBackdropPress={() => {
        toggleIsTeacherControl()
        removeCurrentTeacherOption()
      }}
      isVisible={isTeacherControlOpen}
      animationIn="slideInDown"
      animationOut="slideOutUp"
      backdropOpacity={0}
      style={styles.modal}
      coverScreen={false}
    >
      <DS.Frame style={[styles.panelContainer, { marginTop: insets.top }]}>
        <DS.Frame style={styles.panelContent}>
          {currentTeacherOption ? ConfirmOptions[currentTeacherOption] : <TeacherOptions />}
        </DS.Frame>
      </DS.Frame>
    </Modal>
  )
})

const TeacherControlPauseButton = observer(() => {
  const {
    game: { isGamePaused, resumeTheGame, pauseTheGame, isTeacherObservingInPrivateGame, gameStartTime }
  } = useStore()

  const debouncedRef = useRef(
    debounce(
      (paused) => {
        if (paused) {
          resumeTheGame()
        } else {
          console.log('fired paused')
          pauseTheGame()
        }
      },
      1250,
      { leading: true, trailing: false }
    ) // leading=true allows instant first tap
  )

  const onPlayOrPauseGame = useCallback(() => {
    debouncedRef.current(isGamePaused)
  }, [isGamePaused])

  if (!(isTeacherObservingInPrivateGame && gameStartTime > 0)) return null

  return <TouchableOpacity onPress={onPlayOrPauseGame}>{!isGamePaused ? <PauseSVG /> : <ResumeSVG />}</TouchableOpacity>
})

const TeacherControlToggleButton = observer(() => {
  const { isTeacherControlOpen, toggleIsTeacherControl } = useTeacherControl()
  const {
    game: { isGamePaused, isTeacherInPrivateGame }
  } = useStore()

  if (!isTeacherInPrivateGame) return null

  const iconFill = isGamePaused ? 'black' : DS.palette['Brand/Cream']
  const bgFill = isGamePaused ? DS.palette['Brand/Cream'] : Palette['Shades/Maroon 2']

  return (
    <TouchableOpacity onPress={toggleIsTeacherControl}>
      {!isTeacherControlOpen ? <SettingsSVG iconFill={iconFill} bgFill={bgFill} /> : <CloseSVG />}
    </TouchableOpacity>
  )
})

enum CurrentPlayerOptions {
  giveChips = 'giveChips',
  showCards = 'showCards',
  removePlayer = 'removePlayer'
}

interface PlayerControlStore {
  selectedPlayer?: Player
  isPlayerControlOpen: boolean
  openPlayerControl: (player: Player) => void
  closePlayerControl: () => void
  currentPlayerOption?: CurrentPlayerOptions
  setCurrentPlayerOption: (option: CurrentPlayerOptions) => void
  removeCurrentPlayerOption: () => void
}

export const usePlayerControl = create<PlayerControlStore>((set) => ({
  isPlayerControlOpen: false,
  openPlayerControl: (player) =>
    set(() => ({
      selectedPlayer: player,
      isPlayerControlOpen: true
    })),
  closePlayerControl: () =>
    set(() => ({
      selectedPlayer: undefined,
      isPlayerControlOpen: false,
      currentPlayerOption: undefined
    })),
  setCurrentPlayerOption: (option) =>
    set(() => ({
      currentPlayerOption: option
    })),
  removeCurrentPlayerOption: () =>
    set(() => ({
      currentPlayerOption: undefined
    }))
}))

const PlayerControlPanel = observer(() => {
  const insets = useSafeAreaInsets()
  const {
    isPlayerControlOpen,
    closePlayerControl,
    selectedPlayer,
    currentPlayerOption,
    removeCurrentPlayerOption,
    setCurrentPlayerOption
  } = usePlayerControl()
  const {
    game: {
      removePlayerFromGame,
      showPlayerCardsForTeacher,
      showPlayerCardsForEveryone,
      boardState,
      isPlayerCardsVisibleByTeacher,
      isPlayerCardsVisibleByEveryone,
      isCardsVisibleByEveryone,
      isCardsVisibleByTeacher,
      gameStartTime,
      hidePlayerCards,
      doBuyForPlayer,
      isGameOver,
      isPreGame,
      isTeacherObservingInPrivateGame,
      isTeacherPlayingInPrivateGame
    }
  } = useStore()

  if (!(isTeacherObservingInPrivateGame && selectedPlayer)) return null

  const onPressRemovePlayer = async () => {
    await removePlayerFromGame()
    closePlayerControl()
  }

  const isShowCardsDisabled =
    isCardsVisibleByEveryone ||
    isCardsVisibleByTeacher ||
    boardState === BoardStates.Showdown.tag ||
    !!isTeacherPlayingInPrivateGame ||
    selectedPlayer.isWaiting() ||
    isGameOver ||
    isPreGame ||
    selectedPlayer.isFolded() ||
    selectedPlayer.isOut()

  const isPlayerCardsShowing =
    isPlayerCardsVisibleByTeacher(selectedPlayer.id) || isPlayerCardsVisibleByEveryone(selectedPlayer.id)

  const onShowCardsPrivately = () => {
    if (gameStartTime > 0) {
      showPlayerCardsForTeacher(selectedPlayer.id)
    }
    removeCurrentPlayerOption()
    closePlayerControl()
  }

  const onShowCardsPublicly = () => {
    if (gameStartTime > 0) {
      showPlayerCardsForEveryone(selectedPlayer.id)
    }
    removeCurrentPlayerOption()
    closePlayerControl()
  }

  const onHideCards = () => {
    hidePlayerCards(selectedPlayer.id)
    closePlayerControl()
  }

  const showCardsProps = {
    disabled: isShowCardsDisabled,
    icon: <ShowCardsSVG />,
    label: isPlayerCardsShowing ? 'HIDE CARDS' : 'SHOW CARDS',
    onPress: () => (isPlayerCardsShowing ? onHideCards() : setCurrentPlayerOption(CurrentPlayerOptions.showCards))
  }

  const isGiveChipsDisabled = canReceiveChips(selectedPlayer.state) && (isGameOver || isPreGame)
  const giveChipsProps = {
    disabled: isGiveChipsDisabled,
    icon: <GiveChipsSVG />,
    label: 'GIVE CHIPS',
    onPress: isGiveChipsDisabled ? () => null : () => setCurrentPlayerOption(CurrentPlayerOptions.giveChips)
  }

  const onPressGiveChipsToPlayer = debounce((amount: number) => {
    doBuyForPlayer(amount, selectedPlayer.userId, true)
    closePlayerControl()
  })

  const PlayerOptions = () => {
    return (
      <>
        <DS.Frame style={styles.panelPlayerContentRow}>
          <DS.Text variant="Caption/Bold">{selectedPlayer?.name}</DS.Text>
          <TouchableOpacity onPress={closePlayerControl}>
            <DS.Icon name="Close" width={24} height={24} color="Global/Black" />
          </TouchableOpacity>
        </DS.Frame>
        <DS.Frame style={styles.panelPlayerContentRow}>
          <PanelButton
            icon={giveChipsProps.icon}
            label={giveChipsProps.label}
            onPress={giveChipsProps.onPress}
            disabled={giveChipsProps.disabled}
          />
          <PanelButton
            icon={showCardsProps.icon}
            label={showCardsProps.label}
            onPress={showCardsProps.onPress}
            disabled={showCardsProps.disabled}
          />
          <PanelButton
            icon={<EndGameSVG />}
            label="REMOVE PLAYER"
            onPress={() => setCurrentPlayerOption(CurrentPlayerOptions.removePlayer)}
          />
        </DS.Frame>
      </>
    )
  }

  const RemovePlayer = () => {
    return (
      <DS.Frame style={styles.panelPlayerContentRow}>
        <DS.Frame style={styles.panelPlayerConfirmLabelContainer}>
          <DS.Text variant="Body Medium/Medium" numberOfLines={2} ellipsizeMode="tail">
            Remove {selectedPlayer?.name} from table
          </DS.Text>
        </DS.Frame>
        <DS.Frame style={styles.panelPlayerConfirmButtonContainer}>
          <DS.Button
            style={{ width: 69, height: 38 }}
            textColor="Global/Black"
            variant="Medium/Secondary"
            type="On Light"
            onPress={removeCurrentPlayerOption}
          >
            No
          </DS.Button>
          <DS.Button
            style={{ width: 70, height: 38 }}
            variant="Medium/Primary"
            type="On Light"
            onPress={onPressRemovePlayer}
          >
            Yes
          </DS.Button>
        </DS.Frame>
      </DS.Frame>
    )
  }

  const ConfirmOptions = {
    [CurrentPlayerOptions.removePlayer]: <RemovePlayer />,
    [CurrentPlayerOptions.showCards]: (
      <ShowCards
        onPressBack={removeCurrentPlayerOption}
        onPressPrivate={onShowCardsPrivately}
        onPressPublic={onShowCardsPublicly}
      />
    ),
    [CurrentPlayerOptions.giveChips]: (
      <GiveChips
        onPressBack={removeCurrentPlayerOption}
        onPressGiveChip={onPressGiveChipsToPlayer}
        amounts={[2, 5, 10]}
      />
    )
  }

  return (
    <Modal
      onBackdropPress={closePlayerControl}
      isVisible={isPlayerControlOpen}
      animationIn="slideInDown"
      animationOut="slideOutUp"
      backdropOpacity={0}
      style={styles.modal}
      coverScreen={false}
    >
      <DS.Frame style={[styles.panelContainer, { marginTop: insets.top }]}>
        <DS.Frame style={styles.panelContent}>
          {currentPlayerOption ? ConfirmOptions[currentPlayerOption] : <PlayerOptions />}
        </DS.Frame>
      </DS.Frame>
    </Modal>
  )
})

export const TeacherControl = {
  Panel: TeacherControlPanel,
  ToggleButton: TeacherControlToggleButton,
  PauseButton: TeacherControlPauseButton,
  PlayerPanel: PlayerControlPanel
}

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-start',
    margin: 0,
    flex: 1
  },
  panelContainer: {
    marginHorizontal: DS.spacing[8]
  },
  panelContent: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: DS.spacing[16],
    gap: DS.spacing[8],
    padding: DS.spacing[12],
    width: Platform.OS === 'android' ? '100%' : undefined
  },
  panelContentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: DS.spacing[8]
  },
  panelButton: {
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey'],
    borderRadius: DS.spacing[8],
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: DS.spacing[8],
    gap: DS.spacing[4]
  },
  panelButtonLabel: {
    textAlign: 'center'
  },
  panelPlayerContentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: DS.spacing[8]
  },
  panelPlayerConfirmLabelContainer: {
    flex: 1,
    gap: DS.spacing[8],
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  panelPlayerConfirmButtonContainer: { flexDirection: 'row', gap: DS.spacing[8] },
  panelStepperContainer: {
    backgroundColor: DS.palette['Brand/Light Grey'],
    flexDirection: 'row',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    borderRadius: DS.spacing[8]
  },
  panelStepperButton: { width: 40, height: 32, justifyContent: 'center', alignItems: 'center' }
})
